import { api } from './api';
import {
  BOOK_SKI_PASS_STORAGE_KEY,
  CHECKOUT_STORAGE_KEY,
  CHECKOUT_URL_STORAGE_KEY,
} from '@/shared/constants/storage-keys';
import type { BookingType } from '@/utils/booking-type';

export interface MoneyValue {
  amount: number;
  currency: string;
}

export interface DomainValue {
  id: number;
  name: string;
}

export interface OrderItemDto {
  skipassId: number;
  ageCategory: string;
  birthDate: string;
  firstName: string;
  lastName: string;
  startDate: string;
  days: number;
  domain: DomainValue;
  supplierPrice: MoneyValue;
  skiPassPrice: MoneyValue;
  margin: MoneyValue;
  email: string;
}

export type B2BSyncStatus = 'pending' | 'created' | 'completed' | 'cancelled';

export interface OrderB2BSyncDto {
  domainId: number;
  domainName?: string;
  b2bOrderId?: string;
  status: B2BSyncStatus;
  error?: string;
  attemptedAt?: string;
  syncedAt?: string;
}

export type OrderStatus = 'draft' | 'created' | 'paid' | 'cancelled';

export interface OrderDto {
  orderId: number;
  reference: string;
  b2bSyncs?: OrderB2BSyncDto[];
  freshdeskTicketId?: string;
  promoCode?: string;
  supplierPrice: MoneyValue;
  clientPrice: MoneyValue;
  discount: MoneyValue;
  margin: MoneyValue;
  pricingMarginPercent?: number;
  passes: number;
  passSelections?: CheckoutPassSelectionDto[];
  tripDetails?: CheckoutTripDetailsDto;
  startDate: string;
  skiDays: number;
  domains: DomainValue[];
  status: OrderStatus;
  createdAt: string;
  paidAt?: string | null;
  paidWithMams?: number;
  paidWithCard?: number;
  refundedAmount?: MoneyValue;
  items: OrderItemDto[];
  customerEmail?: string;
}

export type CancelOrderPayload = {
  fullRefund?: boolean;
  refundPercentage?: number;
};

export interface OrdersListResponse {
  items: OrderDto[];
  total: number;
  page: number;
  limit: number;
  stats: OrdersListStats;
  filterMeta: OrdersListFilterMeta;
}

export interface OrdersListStats {
  totalRevenueInCents: number;
  totalMarginInCents: number;
  soldPasses: number;
  totalSoldOrders: number;
  avgPassPriceInCents: number;
  avgPassDays: number;
  totalOrders: number;
  currency: string;
}

export interface OrdersListRangeMeta {
  min: number;
  max: number;
  bars: number[];
}

export interface OrdersListFilterMeta {
  passes: OrdersListRangeMeta;
  skiDays: OrdersListRangeMeta;
  createdAt: {
    min: string | null;
    max: string | null;
  };
  paidAt: {
    min: string | null;
    max: string | null;
  };
}

export type OrdersListParams = {
  page?: number;
  limit?: number;
  reference?: string;
  passMin?: number;
  passMax?: number;
  skiDaysMin?: number;
  skiDaysMax?: number;
  createdFrom?: string;
  createdTo?: string;
  paidFrom?: string;
  paidTo?: string;
  statuses?: string[];
  domains?: string[];
};

export type SkierCountInput = {
  category: string;
  quantity: number;
};

export type CreateOrderDraftPayload = {
  skierCounts: SkierCountInput[];
  skiDays: number;
  startDate: string;
  domain?: DomainValue;
  promoCode?: string;
  reference?: string;
  bookingType?: BookingType;
  skiersLevel?: 'beginner' | 'intermediate' | 'advanced';
  skiStyle?: 'fullDays' | 'balancedPace' | 'relaxed';
};

export type PassSelectionPayload = {
  domain: DomainValue;
  ageCategory: string;
  passOfferId?: number;
  passTitle?: string;
  quantity: number;
};

export type UpdateOrderPassesPayload = {
  selections: PassSelectionPayload[];
};

export type OrderItemDetailsPayload = {
  skipassId?: number;
  ageCategory: string;
  birthDate: string;
  firstName: string;
  lastName: string;
  startDate: string;
  days: number;
  domain: DomainValue;
  supplierPrice?: MoneyValue;
  skiPassPrice?: MoneyValue;
  margin?: MoneyValue;
  email?: string;
};

export type UpdateOrderDetailsPayload = {
  customerEmail: string;
  customerPhone?: string;
  items: OrderItemDetailsPayload[];
};

export type CheckoutOrderDto = {
  orderId: number;
  reference: string;
  checkoutToken: string;
  status: string;
  checkoutStep: string;
  passes: number;
  startDate?: string;
  skiDays?: number;
  customerEmail?: string;
  customerPhone?: string;
  lastActivityAt?: string;
};

export type CheckoutTripDetailsDto = {
  skierCounts: SkierCountInput[];
  skiDays: number;
  startDate: string;
  domain?: DomainValue;
  skiersLevel?: 'beginner' | 'intermediate' | 'advanced';
  skiStyle?: 'fullDays' | 'balancedPace' | 'relaxed';
};

export type CheckoutPassSelectionDto = {
  domain: DomainValue;
  ageCategory: string;
  passOfferId?: number;
  passTitle?: string;
  quantity: number;
};

export type CheckoutOrderItemDto = {
  skipassId: number;
  ageCategory: string;
  birthDate: string;
  firstName: string;
  lastName: string;
  startDate: string;
  days: number;
  domain: DomainValue;
};

export type CheckoutOrderDetailsDto = {
  orderId: number;
  reference?: string;
  promoCode?: string;
  promoCodeInfo?: {
    code: string;
    status: 'active' | 'inactive';
    type?: 'fixed' | 'percentage';
    value?: number;
    minOrderAmount?: number | null;
    inactiveReason?: string;
    applicableDomainIds?: number[];
    restrictedDomainIds?: number[];
    restrictedDomainNames?: string[];
    domainRestrictionPartial?: boolean;
    domainRestrictionInvalid?: boolean;
  };
  status: string;
  checkoutStep: string;
  tripDetails?: CheckoutTripDetailsDto;
  passSelections?: CheckoutPassSelectionDto[];
  items?: CheckoutOrderItemDto[];
  customerEmail?: string;
  customerPhone?: string;
  checkoutUrl?: string;
  /** Set while the order is priced at the fixed price for a logged-in listed A2A account. */
  fixedPrice?: { amount: number; currency: string };
};

export type CheckoutSession = {
  orderId: number;
  checkoutToken: string;
};

export const getCheckoutSession = (): CheckoutSession | null => {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(CHECKOUT_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as CheckoutSession;
    const orderId =
      typeof parsed.orderId === 'number'
        ? parsed.orderId
        : typeof parsed.orderId === 'string'
          ? Number.parseInt(parsed.orderId, 10)
          : NaN;
    if (!Number.isFinite(orderId) || typeof parsed.checkoutToken !== 'string') {
      return null;
    }
    return { orderId, checkoutToken: parsed.checkoutToken };
  } catch {
    return null;
  }
};

export const setCheckoutSession = (session: CheckoutSession): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(CHECKOUT_STORAGE_KEY, JSON.stringify(session));
};

export const getCheckoutUrl = (): string | null => {
  if (typeof window === 'undefined') return null;
  return window.sessionStorage.getItem(CHECKOUT_URL_STORAGE_KEY);
};

export const setCheckoutUrl = (url: string): void => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(CHECKOUT_URL_STORAGE_KEY, url);
};

export const clearCheckoutUrl = (): void => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(CHECKOUT_URL_STORAGE_KEY);
};

export const resetBookingSession = (): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(CHECKOUT_STORAGE_KEY);
  window.localStorage.removeItem(BOOK_SKI_PASS_STORAGE_KEY);
  window.sessionStorage.removeItem(CHECKOUT_URL_STORAGE_KEY);
};

export const ordersService = {
  list: (params?: OrdersListParams) => {
    const searchParams = new URLSearchParams();
    if (params?.page) {
      searchParams.set('page', String(params.page));
    }
    if (params?.limit) {
      searchParams.set('limit', String(params.limit));
    }
    if (params?.reference?.trim()) {
      searchParams.set('reference', params.reference.trim());
    }
    if (params?.passMin != null) {
      searchParams.set('passMin', String(params.passMin));
    }
    if (params?.passMax != null) {
      searchParams.set('passMax', String(params.passMax));
    }
    if (params?.skiDaysMin != null) {
      searchParams.set('skiDaysMin', String(params.skiDaysMin));
    }
    if (params?.skiDaysMax != null) {
      searchParams.set('skiDaysMax', String(params.skiDaysMax));
    }
    if (params?.createdFrom) {
      searchParams.set('createdFrom', params.createdFrom);
    }
    if (params?.createdTo) {
      searchParams.set('createdTo', params.createdTo);
    }
    if (params?.paidFrom) {
      searchParams.set('paidFrom', params.paidFrom);
    }
    if (params?.paidTo) {
      searchParams.set('paidTo', params.paidTo);
    }
    if (params?.statuses && params.statuses.length) {
      params.statuses.forEach((s) => searchParams.append('statuses', String(s)));
    }
    if (params?.domains && params.domains.length) {
      params.domains.forEach((domain) => searchParams.append('domains', domain));
    }
    const queryString = searchParams.toString();
    const endpoint = queryString ? `/api/orders?${queryString}` : '/api/orders';
    return api.get<OrdersListResponse>(endpoint);
  },

  listAll: async (params?: OrdersListParams): Promise<OrderDto[]> => {
    const pageSize = 200;
    const firstPage = await ordersService.list({ ...params, page: 1, limit: pageSize });
    const totalPages = Math.max(1, Math.ceil(firstPage.total / pageSize));

    if (totalPages === 1) {
      return firstPage.items;
    }

    const restPages = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, index) =>
        ordersService.list({ ...params, page: index + 2, limit: pageSize })
      )
    );

    return [firstPage, ...restPages].flatMap((pageResponse) => pageResponse.items);
  },

  listDomainSuggestions: (query: string, limit = 30) => {
    const searchParams = new URLSearchParams();
    if (query.trim()) {
      searchParams.set('query', query.trim());
    }
    if (limit > 0) {
      searchParams.set('limit', String(limit));
    }
    const queryString = searchParams.toString();
    const endpoint = queryString ? `/api/orders/domains?${queryString}` : '/api/orders/domains';
    return api.get<string[]>(endpoint);
  },

  getById: (orderId: number) => api.get<OrderDto>(`/api/orders/${String(orderId)}`),

  cancelOrder: (orderId: number, payload?: CancelOrderPayload) =>
    api.patch<OrderDto | { removed: true; orderId: number }>(
      `/api/orders/${String(orderId)}/cancel`,
      payload ?? {}
    ),

  createDraft: (payload: CreateOrderDraftPayload) =>
    api.post<CheckoutOrderDto>('/api/orders/checkout/draft', payload),

  updatePasses: (orderId: number, checkoutToken: string, payload: UpdateOrderPassesPayload) =>
    api.patch<CheckoutOrderDto>(
      `/api/orders/checkout/${String(orderId)}/passes`,
      payload,
      { 'x-checkout-token': checkoutToken }
    ),

  updateTripDetails: (
    orderId: number,
    checkoutToken: string,
    payload: CreateOrderDraftPayload
  ) =>
    api.patch<CheckoutOrderDto>(
      `/api/orders/checkout/${String(orderId)}/trip`,
      payload,
      { 'x-checkout-token': checkoutToken }
    ),

  updateDetails: (
    orderId: number,
    checkoutToken: string,
    payload: UpdateOrderDetailsPayload
  ) =>
    api.patch<CheckoutOrderDetailsDto>(
      `/api/orders/checkout/${String(orderId)}/details`,
      payload,
      { 'x-checkout-token': checkoutToken }
    ),

  getCheckout: (orderId: number, checkoutToken: string): Promise<CheckoutOrderDetailsDto> =>
    api.get<CheckoutOrderDetailsDto>(`/api/orders/checkout/${String(orderId)}`, {
      'x-checkout-token': checkoutToken,
    }),

  getCardPaymentStatus: (orderId: number, checkoutToken: string) =>
    api.get<{ status: 'paid' | 'pending' | 'failed' }>(
      `/api/orders/checkout/${String(orderId)}/card-payment`,
      { 'x-checkout-token': checkoutToken },
    ),

  /** Starts the next processor after Exactly returns a failed payment. `url` is null when there is no next step. */
  startCardPaymentFallback: (orderId: number, checkoutToken: string) =>
    api.post<{ url: string | null }>(
      `/api/orders/checkout/${String(orderId)}/card-payment/fallback`,
      {},
      { 'x-checkout-token': checkoutToken },
    ),

  /**
   * After an A2A login change: fixed price for a listed account, regular price otherwise.
   * `changed` is true when the order was re-priced (then `checkoutUrl` is the new card checkout).
   */
  syncLoyaltyPrice: (orderId: number, checkoutToken: string) =>
    api.post<CheckoutOrderDetailsDto & { applied: boolean; changed: boolean }>(
      `/api/orders/checkout/${String(orderId)}/loyalty-price`,
      {},
      { 'x-checkout-token': checkoutToken }
    ),
};
